import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Keycloak from "keycloak-js";
import { registerTokenProvider } from "../utils/tokenProvider";
import { TenantConfigContext } from "./TenantConfigContext";

const AuthContext = createContext(null);
const SUPERADMIN_ROLE = "superadmin";

function extractRoles(tokenParsed) {
  const realmRoles = tokenParsed?.realm_access?.roles || [];
  const clientRoles = Object.entries(tokenParsed?.resource_access || {}).flatMap(
    ([, access]) => access?.roles || []
  );

  return [...new Set([...realmRoles, ...clientRoles])];
}

export function AuthProvider({ children }) {
  const tenantConfig = useContext(TenantConfigContext);
  const keycloakRef = useRef(null);

  const [auth, setAuth] = useState({
    loading: true,
    isAuthenticated: false,
    token: null,
    user: null,
    roles: [],
  });

  const refreshTimer = useRef(null);
  const loginTriggered = useRef(false);

  const clearRefreshLoop = useCallback(() => {
    if (refreshTimer.current) {
      clearInterval(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const handleLocalLogout = useCallback(() => {
    clearRefreshLoop();
    setAuth({
      loading: false,
      isAuthenticated: false,
      token: null,
      user: null,
      roles: [],
    });
    loginTriggered.current = false;
  }, [clearRefreshLoop]);

  const setupTokenProvider = useCallback(() => {
    registerTokenProvider(async () => {
      const keycloak = keycloakRef.current;
      if (!keycloak) return null;

      try {
        if (keycloak.isTokenExpired(30)) {
          await keycloak.updateToken(30);
        }
        return keycloak.token || null;
      } catch (e) {
        console.error("tokenProvider: failed to refresh token", e);
        return null;
      }
    });
  }, []);

  const startRefreshLoop = useCallback(() => {
    const keycloak = keycloakRef.current;
    if (refreshTimer.current || !keycloak || !keycloak.authenticated) return;
    refreshTimer.current = setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          const t = keycloak.tokenParsed ?? {};
          const roles = extractRoles(t);
          setAuth((prev) => ({
            ...prev,
            token: keycloak.token,
            user: t,
            roles,
          }));
        }
      } catch {
        if (!loginTriggered.current) {
          loginTriggered.current = true;
          keycloak.login({ redirectUri: `${window.location.origin}/` });
        }
      }
    }, 10000);
  }, []);

  useEffect(() => {
    if (!tenantConfig) {
      return;
    }

    const keycloak = new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL,
      realm: process.env.REACT_APP_KEYCLOAK_REALM,
      clientId: tenantConfig.keycloakClientId,
    });

    keycloakRef.current = keycloak;
    let isMounted = true;

    (async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          checkLoginIframe: false,
        });

        if (!isMounted) return;

        if (authenticated) {
          const t = keycloak.tokenParsed ?? {};
          const roles = extractRoles(t);

          setAuth({
            loading: false,
            isAuthenticated: true,
            token: keycloak.token,
            user: t,
            roles,
          });

          setupTokenProvider();
          startRefreshLoop();
        } else {
          setAuth({
            loading: false,
            isAuthenticated: false,
            token: null,
            user: null,
            roles: [],
          });
        }
      } catch (e) {
        console.error("Keycloak init failed:", e);
        if (!isMounted) return;
        setAuth({
          loading: false,
          isAuthenticated: false,
          token: null,
          user: null,
          roles: [],
        });
      }
    })();

    keycloak.onAuthSuccess = () => {
      if (!isMounted) return;
      const t = keycloak.tokenParsed ?? {};
      const roles = extractRoles(t);
      setAuth({
        loading: false,
        isAuthenticated: true,
        token: keycloak.token,
        user: t,
        roles,
      });
      setupTokenProvider();
    };

    keycloak.onAuthLogout = () => {
      if (!isMounted) return;
      handleLocalLogout();
    };

    keycloak.onTokenExpired = async () => {
      try {
        await keycloak.updateToken(30);
        const t = keycloak.tokenParsed ?? {};
        const roles = extractRoles(t);
        setAuth((prev) => ({
          ...prev,
          token: keycloak.token,
          user: t,
          roles,
        }));
      } catch {
        if (!loginTriggered.current) {
          loginTriggered.current = true;
          keycloak.login({ redirectUri: `${window.location.origin}/` });
        }
      }
    };

    const visHandler = () => {
      const kc = keycloakRef.current;
      if (document.visibilityState === "visible" && kc?.authenticated) {
        startRefreshLoop();
      } else {
        clearRefreshLoop();
      }
    };
    document.addEventListener("visibilitychange", visHandler);

    return () => {
      isMounted = false;
      clearRefreshLoop();
      document.removeEventListener("visibilitychange", visHandler);
    };
  }, [tenantConfig, clearRefreshLoop, handleLocalLogout, setupTokenProvider, startRefreshLoop]);

  const value = useMemo(() => {
    const keycloak = keycloakRef.current;

    const getToken = async () => {
      if (!keycloak) return null;
      try {
        await keycloak.updateToken(30);
        return keycloak.token;
      } catch {
        if (!loginTriggered.current) {
          loginTriggered.current = true;
          await keycloak.login({ redirectUri: `${window.location.origin}/` });
        }
        return null;
      }
    };

    const hasRole = (role) => {
      if (!keycloak || !keycloak.authenticated) return false;
      if (role !== SUPERADMIN_ROLE && auth.roles?.includes(SUPERADMIN_ROLE)) return true;
      if (keycloak.hasRealmRole?.(role)) return true;
      return auth.roles?.includes(role);
    };

    const login = (opts) => keycloak?.login(opts);

    const logout = async (opts = {}) => {
      if (!keycloak) {
        handleLocalLogout();
        return;
      }

      try {
        await keycloak.logout({
          redirectUri: window.location.origin,
          ...opts,
        });
      } catch (e) {
        console.error("Keycloak logout failed:", e);
      } finally {
        handleLocalLogout();
      }
    };

    return { auth, getToken, hasRole, login, logout, keycloak };
  }, [auth, handleLocalLogout]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth() must be used within an <AuthProvider>");
  }
  return context;
}
